import React, { useCallback, useEffect, useState } from "react";
import { createTeamEmployee, deleteTeamEmployee, listTeamEmployees, updateTeamEmployee } from "../api/teamApi";

function getApiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;
  const message = error?.response?.data?.message;
  return detail || message || error?.message || fallback;
}

export default function ClientTeamDashboard({ organizations = [], onNotify, onBack }) {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [orgAccessEditorEmployee, setOrgAccessEditorEmployee] = useState(null);
  const [orgAccessDraftOrgIds, setOrgAccessDraftOrgIds] = useState([]);
  const [isSavingOrgAccess, setIsSavingOrgAccess] = useState(false);

  const [newEmail, setNewEmail] = useState("");
  const [newFullName, setNewFullName] = useState("");
  const [newAuthProvider, setNewAuthProvider] = useState("oauth");
  const [newTemporaryPassword, setNewTemporaryPassword] = useState("");
  const [newForcePasswordChange, setNewForcePasswordChange] = useState(false);
  const [newAssignedOrgIds, setNewAssignedOrgIds] = useState([]);

  const loadEmployees = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listTeamEmployees();
      setEmployees(data?.employees || []);
    } catch (error) {
      onNotify?.(`Failed to load employees: ${getApiErrorMessage(error, "Request failed")}`, "error");
    } finally {
      setIsLoading(false);
    }
  }, [onNotify]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  function onToggleOrgForNewEmployee(orgId) {
    setNewAssignedOrgIds((current) => {
      if (current.includes(orgId)) {
        return current.filter((item) => item !== orgId);
      }
      return [...current, orgId];
    });
  }

  async function onCreateEmployee(event) {
    event.preventDefault();
    try {
      await createTeamEmployee({
        email: newEmail.trim().toLowerCase(),
        full_name: newFullName.trim() || null,
        auth_provider: newAuthProvider,
        temporary_password: newAuthProvider === "password" ? newTemporaryPassword.trim() : null,
        force_password_change: newAuthProvider === "password" ? newForcePasswordChange : false,
        assigned_org_ids: newAssignedOrgIds,
        is_active: true,
      });
      setNewEmail("");
      setNewFullName("");
      setNewAuthProvider("oauth");
      setNewTemporaryPassword("");
      setNewForcePasswordChange(false);
      setNewAssignedOrgIds([]);
      onNotify?.("Employee account created.", "success");
      await loadEmployees();
    } catch (error) {
      onNotify?.(`Failed to create employee: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  }

  async function onToggleEmployeeBlock(employee) {
    try {
      await updateTeamEmployee(employee.email, { is_active: !employee.is_active });
      onNotify?.("Employee status updated.", "success");
      await loadEmployees();
    } catch (error) {
      onNotify?.(`Failed to update employee: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  }

  async function onSaveEmployeeOrgAssignments(employee, assignedOrgIds) {
    try {
      await updateTeamEmployee(employee.email, { assigned_org_ids: assignedOrgIds });
      onNotify?.("Employee organization access updated.", "success");
      await loadEmployees();
    } catch (error) {
      onNotify?.(`Failed to update employee org access: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  }

  function openOrgAccessEditor(employee) {
    const currentOrgIds = Array.isArray(employee?.assigned_org_ids)
      ? employee.assigned_org_ids.map((item) => String(item))
      : [];
    setOrgAccessEditorEmployee(employee);
    setOrgAccessDraftOrgIds(currentOrgIds);
  }

  function closeOrgAccessEditor() {
    setOrgAccessEditorEmployee(null);
    setOrgAccessDraftOrgIds([]);
    setIsSavingOrgAccess(false);
  }

  function onToggleOrgForEditor(orgId) {
    setOrgAccessDraftOrgIds((current) => {
      if (current.includes(orgId)) {
        return current.filter((item) => item !== orgId);
      }
      return [...current, orgId];
    });
  }

  async function onSubmitOrgAccessEditor() {
    if (!orgAccessEditorEmployee) {
      return;
    }

    try {
      setIsSavingOrgAccess(true);
      await onSaveEmployeeOrgAssignments(orgAccessEditorEmployee, orgAccessDraftOrgIds);
      closeOrgAccessEditor();
    } finally {
      setIsSavingOrgAccess(false);
    }
  }

  async function onDeleteEmployee(employee) {
    const confirmed = window.confirm(
      `Delete employee ${employee.email}? This removes the account and cannot be undone.`
    );
    if (!confirmed) {
      return;
    }

    const typedEmail = window.prompt(
      `For safety, type the employee email to confirm deletion:\n${employee.email}`
    );
    if (typedEmail === null) {
      return;
    }

    if (typedEmail.trim().toLowerCase() !== String(employee.email || "").trim().toLowerCase()) {
      onNotify?.("Deletion canceled: email confirmation did not match.", "info");
      return;
    }

    try {
      await deleteTeamEmployee(employee.email);
      onNotify?.("Employee account deleted.", "success");
      await loadEmployees();
    } catch (error) {
      onNotify?.(`Failed to delete employee: ${getApiErrorMessage(error, "Request failed")}`, "error");
    }
  }

  return (
    <div className="developer-shell">
      <div className="developer-header">
        <div>
          <h2>Client Team Dashboard</h2>
          <p className="dev-header-sub">Create employee accounts, assign organization access, and control account status.</p>
        </div>
        <button type="button" className="dev-btn" onClick={onBack}>Back To App</button>
      </div>

      <div className="dev-stat-strip">
        <div className="dev-stat-chip">Employees: {employees.length}</div>
        <div className="dev-stat-chip">Organizations: {organizations.length}</div>
      </div>

      <div className="developer-grid">
        <section className="developer-card">
          <h3>Create Employee Account</h3>
          <p className="dev-note">Create employee logins and assign one or multiple organizations.</p>
          <p className="dev-note">Client manager/admin accounts are managed in Developer Dashboard and cannot be edited here.</p>
          <form className="developer-form" onSubmit={onCreateEmployee}>
            <input
              type="email"
              placeholder="employee@example.com"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              required
            />
            <input
              type="text"
              placeholder="Full name"
              value={newFullName}
              onChange={(event) => setNewFullName(event.target.value)}
            />
            <select value={newAuthProvider} onChange={(event) => setNewAuthProvider(event.target.value)}>
              <option value="oauth">oauth</option>
              <option value="password">username/password</option>
            </select>
            {newAuthProvider === "password" ? (
              <>
                <input
                  type="password"
                  placeholder="Temporary password"
                  value={newTemporaryPassword}
                  onChange={(event) => setNewTemporaryPassword(event.target.value)}
                  required
                />
                <label className="dev-check">
                  <input
                    type="checkbox"
                    checked={newForcePasswordChange}
                    onChange={(event) => setNewForcePasswordChange(event.target.checked)}
                  />
                  Force password update on first login
                </label>
              </>
            ) : null}

            <div className="dev-auth-methods">
              <strong>Organization Access</strong>
              {!organizations.length ? <p className="dev-note">No organizations available.</p> : null}
              {organizations.map((org) => (
                <label key={org.org_id} className="dev-check">
                  <input
                    type="checkbox"
                    checked={newAssignedOrgIds.includes(String(org.org_id))}
                    onChange={() => onToggleOrgForNewEmployee(String(org.org_id))}
                  />
                  {org.name}
                </label>
              ))}
            </div>

            <button type="submit" className="dev-btn">Create Employee</button>
          </form>
        </section>

        <section className="developer-card">
          <h3>Managed Employees</h3>
          <p className="dev-note">{isLoading ? "Refreshing..." : `Total employees: ${employees.length}`}</p>
          <div className="dev-user-list">
            {employees.map((employee) => (
              <div key={employee.email} className="dev-user-row team-employee-row">
                <div className="team-employee-head">
                  <strong>{employee.email}</strong>
                  <p className="dev-note">{employee.full_name || "No name"} | {employee.auth_provider}</p>
                </div>
                <div className="team-employee-controls">
                  <span className={`dev-pill ${employee.is_active ? "enabled" : "disabled"}`}>
                    {employee.is_active ? "Active" : "Blocked"}
                  </span>
                  <button type="button" className="dev-btn" onClick={() => onToggleEmployeeBlock(employee)}>
                    {employee.is_active ? "Block" : "Allow"}
                  </button>
                  <button
                    type="button"
                    className="dev-btn"
                    onClick={() => openOrgAccessEditor(employee)}
                  >
                    Edit Org Access
                  </button>
                  <button
                    type="button"
                    className="dev-btn danger"
                    onClick={() => onDeleteEmployee(employee)}
                  >
                    Delete Employee
                  </button>
                </div>
                <div className="team-org-tags">
                  {(employee.assigned_org_names || []).length ? (
                    (employee.assigned_org_names || []).map((orgName) => (
                      <span key={`${employee.email}-${orgName}`} className="team-org-tag">{orgName}</span>
                    ))
                  ) : (
                    <span className="team-org-empty">No org access</span>
                  )}
                </div>
              </div>
            ))}
            {!employees.length ? <p className="dev-note">No employee accounts created yet.</p> : null}
          </div>
        </section>
      </div>

      {orgAccessEditorEmployee ? (
        <div className="org-popup-overlay" onClick={closeOrgAccessEditor}>
          <div className="org-popup-card team-access-popup" onClick={(event) => event.stopPropagation()}>
            <div className="org-popup-head">
              <h3>Edit Organization Access</h3>
              <button type="button" className="org-popup-close" onClick={closeOrgAccessEditor}>
                Close
              </button>
            </div>
            <p className="org-popup-sub">
              Select organizations for {orgAccessEditorEmployee.email}. Turn access on or off using the toggles below.
            </p>

            <div className="team-org-selector-list">
              {!organizations.length ? (
                <p className="dev-note">No organizations found. Create or approve organizations first.</p>
              ) : null}
              {organizations.map((org) => {
                const orgId = String(org.org_id || "");
                const isEnabled = orgAccessDraftOrgIds.includes(orgId);
                return (
                  <div key={orgId} className="team-org-selector-row">
                    <div className="team-org-selector-copy">
                      <strong>{org.name}</strong>
                      <small>ID: {orgId}</small>
                    </div>
                    <button
                      type="button"
                      className={`dev-toggle ${isEnabled ? "enabled" : "disabled"}`}
                      onClick={() => onToggleOrgForEditor(orgId)}
                      aria-pressed={isEnabled}
                    >
                      <span className="dev-toggle-knob" />
                      <span>{isEnabled ? "Enabled" : "Disabled"}</span>
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="org-popup-actions">
              <button
                type="button"
                className="org-hub-primary-btn with-icon"
                onClick={onSubmitOrgAccessEditor}
                disabled={isSavingOrgAccess}
              >
                <span className="org-btn-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20">
                    <path d="M4 10l4 4 8-8" />
                  </svg>
                </span>
                <span>{isSavingOrgAccess ? "Saving..." : "Save Organization Access"}</span>
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
